import { faInfo, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PresentToAll } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useMeeting } from '../../../../hooks/useMeeting';
import { usePermission } from '../../../../hooks/usePermission';
import { Booth } from '../../../../interfaces/interfaces';
import { fetchAndSetAdminBooth } from '../../../../reduxToolkit/slices/adminSlice';
import { useDispatch, useSelector } from '../../../../reduxToolkit/typedRedux';
import { formatUTCDate } from '../../../../services/helper';
import { deleteMeeting } from '../../../../services/services';
import VyTooltip from '../../../common/VyTooltip';
import { MeetingConfigButton } from '../BoothInfo/MeetingConfigButton';
import { MeetingPopoverOptions } from '../BoothInfo/MeetingPopoverOptions';
import { PresentationInfos } from './PresentationInfos';
import PresentationSlotSelect from '../../PresentationSlotSelect';
import { useRole } from '../../../../hooks/useRole';
import PresentationEditComponent from '../../../event/templates/VystemLight/UIComponents/PresentationUiComponent/PublicPresentation/PresentationEditComponent';

interface PresentationProps {
  id: string;
  cardWidth?: number;
  cardHeight?: number;
  withoutChatrooms?: boolean;
  booth?: Booth;
  isInline?: boolean;
  onRefresh?: () => void;
}

const Presentation = (props: PresentationProps) => {
  const { withoutChatrooms = false, booth: boothProp, isInline = false, onRefresh } = props;

  const booth = boothProp ?? useSelector((state) => state.admin.userbooth);
  const event = useSelector((state) => state.admin.event ?? state.common.event);
  const user = useSelector((state) => state.common.user);

  const dispatch = useDispatch();
  const { isEventHost } = usePermission();
  const { redirectToRoom } = useMeeting();

  const presentation = booth?.presentationrooms?.find((presentationRoom) => presentationRoom._id === props.id);
  const [isPresentationInfoModalOpen, setIsPresentationInfoModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation(['account', 'common']);
  const { isUserEventHost } = useRole();

  function refreshBoothData() {
    if (booth && !isInline) dispatch(fetchAndSetAdminBooth({ boothid: booth._id }));
    onRefresh?.();
  }

  async function deletePresentation(meetingroomid: string) {
    setIsLoading(true);
    try {
      await deleteMeeting(meetingroomid);
    } catch (err) {
      console.error(err);
    }
    setIsLoading(false);

    refreshBoothData();
  }

  if (!presentation) return <></>;

  const buttonBackgroundStyle = 'rounded p-1 hover:bg-gray-700 cursor-pointer [&>*]:text-white [&>*]:text-[12px] flex bg-slate-500 justify-center items-center';

  return (
    <div className="max-w-full w-fit">
      <div className="w-full max-w-full my-4 duration-200 p-4 bg-cover min-h-[200px] flex flex-col justify-between relative hover:shadow-2xl sm:w-96">
        <PresentationEditComponent meetingroom={presentation} useBooth />
        <div className="mt-2">
          <div className="space-y-2">{isInline && isEventHost(user) && <PresentationSlotSelect id={props.id} booth={booth} onRefresh={onRefresh} />}</div>
        </div>
        {!isInline && (
          <div className="z-50 flex gap-2 ml-4 text-white">
            <span>{t('account:presentation.your-slot')}: </span>
            {presentation.slot?.datetime_start ? (
              <p>{formatUTCDate(new Date(presentation.slot?.datetime_start), 'HH:mm')}</p>
            ) : (
              <VyTooltip title={t('account:presentation.is-created-by-host')}>
                <p>{t('account:presentation.no-time-yet')}</p>
              </VyTooltip>
            )}
          </div>
        )}
      </div>
      {!isLoading && (
        <div className="flex items-center justify-center gap-4">
          {!presentation.slot && isUserEventHost() && (
            <button
              data-cy={`delete-presentation-${presentation?._id}`}
              onClick={() => deletePresentation(presentation.id)}
              className="text-slate-600 text-lg cursor-pointer bg-transparent hover:text-[#0285c7] hover:scale-110">
              <VyTooltip title={t('common:delete')}>
                <FontAwesomeIcon icon={faTrashAlt} />
              </VyTooltip>
            </button>
          )}
          {!withoutChatrooms && (
            <a
              onClick={() => redirectToRoom(presentation)}
              className="text-slate-600 text-lg cursor-pointer hover:text-[#0285c7] hover:scale-110"
              data-cy={`join-presentation-${presentation?._id}`}>
              <VyTooltip title={t('account:presentation.now-to-presentation')}>
                <PresentToAll />
              </VyTooltip>
            </a>
          )}
          {event?.settings.showBoothStats && isUserEventHost() && (
            <button
              onClick={() => {
                setIsPresentationInfoModalOpen(true);
              }}
              className="text-slate-600 text-lg cursor-pointer bg-transparent hover:text-[#0285c7] hover:scale-110">
              <VyTooltip title={t('account:presentation.details')}>
                <FontAwesomeIcon icon={faInfo} />
              </VyTooltip>
            </button>
          )}
          <MeetingConfigButton meetingroom={presentation} />
          <MeetingPopoverOptions meetingroom={presentation} booth={booth} />
        </div>
      )}
      {isLoading && (
        <div className="flex justify-center">
          <CircularProgress />
        </div>
      )}
      {isPresentationInfoModalOpen && <PresentationInfos onCloseModal={() => setIsPresentationInfoModalOpen(false)} presentationId={presentation._id} />}
    </div>
  );
};

export default Presentation;
